import { deleteCropFromField, updateCropInField } from '../../../farm/store/actions/field.actions'
import { CropService } from '../../../../../shared/crop/services/crop.service'
import { startLoadingCrops, stopLoadingCrops } from '../../../../../state/loading/loading.actions'
import { LoadingState } from '../../../../../state/loading/loading.reducer'
import { ToastService, requireDefined } from '@agroone-front/shared'
import { Crop, VirtualCrop } from '@agroone/entities'
import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'
import { catchError, distinctUntilChanged, map, mergeMap, of, tap } from 'rxjs'
import {
  addCrop,
  addCropFailure,
  addCropSuccess,
  deleteCrop,
  deleteCropError,
  deleteCropSuccess,
  loadCrops,
  loadCropsFailure,
  loadCropsSuccess,
  patchCrop,
  patchCropFailure,
  patchCropSuccess,
  uncloseCrop,
  uncloseCropFailure,
  uncloseCropSuccess,
  updateCrop,
  updateCropFailure,
  updateCropSuccess,
} from '../actions/crop.actions'

@Injectable()
export class CropEffects {
  private actions$ = inject(Actions)
  private cropService = inject(CropService)
  private translate = inject(TranslateService)
  private loadingStore = inject<Store<LoadingState>>(Store)
  private toastService = inject(ToastService)

  public getCrops$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCrops),
      distinctUntilChanged(),
      tap(() => this.loadingStore.dispatch(startLoadingCrops())),
      mergeMap((parameters) =>
        // streamAll emits page by page; the first emission stops the spinner so the list shows ASAP,
        // and each subsequent page grows the (virtualized) list.
        this.cropService.streamAll(parameters).pipe(
          tap(() => this.loadingStore.dispatch(stopLoadingCrops())),
          map((crops) => loadCropsSuccess({ crops })),
          catchError((e) => {
            this.loadingStore.dispatch(stopLoadingCrops())
            return of(loadCropsFailure({ error: `${e}` }))
          }),
        ),
      ),
    ),
  )

  public deleteCrop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCrop),
      mergeMap(({ cropId, fieldId }) =>
        this.cropService.delete(cropId).pipe(
          map(() => {
            this.toastService.showToastSuccess(this.translate.instant('CROPS.SUCCESSFULLY_DELETED'))
            return deleteCropSuccess({ payload: { cropId, fieldId } })
          }),
          catchError((e) => {
            this.toastService.showToastError(this.translate.instant('CROPS.DELETE_FAILED'))
            return of(deleteCropError({ error: `${e}` }))
          }),
        ),
      ),
    ),
  )

  public deleteCropSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCropSuccess),
      map(({ payload }) => deleteCropFromField({ fieldId: payload.fieldId, cropId: payload.cropId })),
    ),
  )

  public uncloseCrop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uncloseCrop),
      mergeMap(({ crop }) =>
        this.cropService.unclose(requireDefined(crop.id, 'the crop id')).pipe(
          mergeMap(() => {
            this.toastService.showToastSuccess(this.translate.instant('CROP.SUCCESSFULLY_REOPENED'))
            const updatedCrop = { ...crop, endDate: null } as Crop
            return of(
              uncloseCropSuccess({ payload: { crop: updatedCrop } }),
              updateCropInField({ fieldId: crop.fieldId, crop: updatedCrop }),
            )
          }),
          catchError((error) => {
            this.toastService.showToastError(this.translate.instant('CROP.REOPEN_FAILED'))
            return of(uncloseCropFailure({ error }))
          }),
        ),
      ),
    ),
  )

  public addCrop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCrop),
      mergeMap(({ payload }) => {
        if (payload?.options?.insert === false) {
          return of(
            addCropSuccess({
              payload: {
                crop: payload.crop,
                fieldId: payload.crop.fieldId,
                message: this.translate.instant('FIELD.CROP.SEND_SUCCESS'),
                loading: false,
              },
            }),
          )
        }
        return this.cropService.add(payload.crop).pipe(
          map((crop) =>
            addCropSuccess({
              payload: {
                crop,
                fieldId: crop.fieldId,
                message: this.translate.instant('FIELD.CROP.SEND_SUCCESS'),
                loading: false,
                virtualCropId: payload.options?.virtualCropId,
              },
            }),
          ),
          catchError((e) =>
            of(
              addCropFailure({
                error: `${e}`,
                loading: false,
                message: this.translate.instant('FIELD.CROP.SEND_FAILURE'),
              }),
            ),
          ),
        )
      }),
    ),
  )

  public updateCrop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCrop),
      mergeMap(({ payload }) =>
        this.cropService.update(new Crop(payload.crop)).pipe(
          map((crop) =>
            updateCropSuccess({
              payload: {
                crop,
                fieldId: crop?.fieldId,
                message: 'Crop successfully updated',
                loading: false,
              },
            }),
          ),
          catchError((e) => of(updateCropFailure({ error: `${e}`, loading: false }))),
        ),
      ),
    ),
  )

  public patchCrop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patchCrop),
      mergeMap(({ payload }) => {
        const item = payload?.crop?.isVirtual ? new VirtualCrop(payload.crop) : new Crop(payload.crop)
        return this.cropService.patch(item, payload.patchValues).pipe(
          map(() =>
            patchCropSuccess({
              payload: {
                crop: { ...item, ...payload.patchValues } as Crop,
                message: payload?.message ? payload?.message : 'Crop successfully patched',
                loading: false,
              },
            }),
          ),
          catchError((e) => of(patchCropFailure({ error: `${e}`, loading: false }))),
        )
      }),
    ),
  )
}
